import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CompanyProvider } from "@/lib/company";
import { BrandingProvider } from "@/lib/branding";
import { EntitlementsProvider } from "@/lib/entitlements";
import { UpgradeModal, PlanPreviewPill } from "@/components/Entitlements";
import { useHostTitle } from "@/lib/useHostTitle";
import { initPwa } from "@/lib/pwa";
import { InstallPromptToast } from "@/components/InstallPrompt";
import Layout from "@/components/Layout";
import VoiceActionReview from "@/components/VoiceActionReview";
import VoiceRecapReview from "@/components/VoiceRecapReview";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import EnterReferral from "@/pages/EnterReferral";
import ReferClickThru from "@/pages/ReferClickThru";
import Share from "@/pages/Share";
import PublicDemoUK from "@/pages/PublicDemoUK";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import AICleanupReview from "@/pages/AICleanupReview";
import CheckRegisterReview from "@/pages/CheckRegisterReview";
import ChatReview from "@/pages/ChatReview";
import { PayrollDashboard, PayrollRuns, PayrollRun, PayrollEmployeeHistory } from "@/pages/Payroll";
import LetsReview from "@/pages/LetsReview";
import NoContactReview from "@/pages/NoContactReview";
import TransferReview from "@/pages/TransferReview";
import Reports from "@/pages/Reports";
import TrialReportsGate from "@/components/TrialReportsGate";
import ReportView from "@/pages/ReportView";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Classes from "@/pages/Classes";
import Projects from "@/pages/Projects";
import ProjectsDashboard from "@/pages/ProjectsDashboard";
import ProjectDetail from "@/pages/ProjectDetail";
import Budgets from "@/pages/Budgets";
import BudgetEditor from "@/pages/BudgetEditor";
import BudgetVsActuals from "@/pages/BudgetVsActuals";
import EstimatesVsActuals from "@/pages/EstimatesVsActuals";
import ArAgingReport from "@/pages/ArAgingReport";
import ApAgingReport from "@/pages/ApAgingReport";
import SalesTaxReport from "@/pages/SalesTaxReport";
import BookToTaxReport from "@/pages/BookToTaxReport";
import TaxLineReport from "@/pages/TaxLineReport";
import ProOnboarding from "@/pages/ProOnboarding";
import Projections from "@/pages/Projections";
import OwnerDashboard from "@/pages/OwnerDashboard";
import CrmOverview from "@/pages/CrmOverview";
import CrmEmail from "@/pages/CrmEmail";
import CrmCalendar from "@/pages/CrmCalendar";
import HomeDashboard from "@/pages/HomeDashboard";
import DealsBoard from "@/pages/DealsBoard";
import CrmSettings from "@/pages/CrmSettings";
import Team from "@/pages/Team";
import TimeLog from "@/pages/TimeLog";
import TeamCalendar from "@/pages/TeamCalendar";
import TimesheetApprovals from "@/pages/TimesheetApprovals";
import JournalEntries from "@/pages/JournalEntries";
import ReviewV2Lab from "@/pages/ReviewV2Lab";
import LabTransactionsCompare from "@/pages/LabTransactionsCompare";
import PrintChecks from "@/pages/PrintChecks";
import NotificationSettings from "@/pages/NotificationSettings";
import { Gate } from "@/components/Entitlements";
import Rules from "@/pages/Rules";
import Onboarding from "@/pages/Onboarding";
import Welcome from "@/pages/Welcome";
import WelcomeSummary from "@/pages/WelcomeSummary";
import WelcomeComplete from "@/pages/WelcomeComplete";
import PaymentsApplication from "@/pages/PaymentsApplication";
import PricingPlans from "@/pages/PricingPlans";
import SuperadminDash from "@/pages/SuperadminDash";
import SuperadminUsage from "@/pages/SuperadminUsage";
import SuperadminStripeWebhooks from "@/pages/SuperadminStripeWebhooks";
import AdminQboGlLab from "@/pages/AdminQboGlLab";
import PartnerDash from "@/pages/PartnerDash";
import PartnerFinancials from "@/pages/PartnerFinancials";
import AdminPartnerDetail from "@/pages/AdminPartnerDetail";
import ProClients from "@/pages/ProClients";
import AdminEnterpriseDetail from "@/pages/AdminEnterpriseDetail";
import AdminClientPayments from "@/pages/AdminClientPayments";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminLeads from "@/pages/AdminLeads";
import AdminAffiliates from "@/pages/AdminAffiliates";
import AdminAffiliateToolkit from "@/pages/AdminAffiliateToolkit";
import BecomeAffiliate from "@/pages/BecomeAffiliate";
import ProAffiliates from "@/pages/ProAffiliates";
import AdminCronRuns from "@/pages/AdminCronRuns";
import AdminEntitlements from "@/pages/AdminEntitlements";
import MyFeedback from "@/pages/MyFeedback";
import { BillingSuccess, BillingCancel } from "@/pages/BillingReturn";
import Invoices from "@/pages/Invoices";
import Estimates from "@/pages/Estimates";
import EstimateEditor from "@/pages/EstimateEditor";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PurchaseOrderEditor from "@/pages/PurchaseOrderEditor";
import InvoiceEditor from "@/pages/InvoiceEditor";
import Bills from "@/pages/Bills";
import BillEditor from "@/pages/BillEditor";
import PurchaseEditor from "@/pages/PurchaseEditor";
import SalesReceiptEditor from "@/pages/SalesReceiptEditor";
import DepositEditor from "@/pages/DepositEditor";
import CreditMemoEditor from "@/pages/CreditMemoEditor";
import RefundReceiptEditor from "@/pages/RefundReceiptEditor";
import SalesReceipts from "@/pages/SalesReceipts";
import CreditMemos from "@/pages/CreditMemos";
import BankMatchReview from "@/pages/BankMatchReview";
import AdvancedModeRoute from "@/components/AdvancedModeRoute";
// TaxLibrary imported by SalesTax; kept only for its default export (used inline).
import Recurring from "@/pages/Recurring";
import Items from "@/pages/Items";
import InventoryPage from "@/pages/InventoryPage";
import SalesReports from "@/pages/SalesReports";
import Payments from "@/pages/Payments";
import Receipts from "@/pages/Receipts";
import Contacts from "@/pages/Contacts";
import CustomerStatements from "@/pages/CustomerStatements";
import LoansPage from "@/pages/LoansPage";
import Connections from "@/pages/Connections";
import QboConnect from "@/pages/QboConnect";
import QboMirror from "@/pages/QboMirror";
import TestQbo from "@/pages/TestQbo";
import VendorCredits from "@/pages/VendorCredits";
import VendorCreditEditor from "@/pages/VendorCreditEditor";
import RefundReceipts from "@/pages/RefundReceipts";
import SalesTax from "@/pages/SalesTax";
import PfcCategoryMap from "@/pages/PfcCategoryMap";
import StatementImportDetail from "@/pages/StatementImportDetail";
import AccountingSettings from "@/pages/AccountingSettings";
import ProductGuard from "@/components/ProductGuard";
import AdminProductLaunches from "@/pages/AdminProductLaunches";
import CompletedActions from "@/pages/CompletedActions";
import PublicBookingPage from "@/pages/PublicBookingPage";
import AuditLog from "@/pages/AuditLog";
import ProSettings from "@/pages/ProSettings";
import MonthClose from "@/pages/MonthClose";
import CockpitLayout from "@/components/CockpitLayout";
import CockpitTodayV7 from "@/pages/CockpitTodayV7";
import CockpitAssistantAll from "@/pages/CockpitAssistantAll";
import CockpitCloseBoard from "@/pages/CockpitCloseBoard";
import CockpitRequests from "@/pages/CockpitRequests";
import Cockpit1099 from "@/pages/Cockpit1099";
import CockpitReports from "@/pages/CockpitReports";
import CockpitPaymentsApps from "@/pages/CockpitPaymentsApps";
import MerchantReviewList from "@/pages/MerchantReviewList";
import MerchantReviewDetail from "@/pages/MerchantReviewDetail";
import MerchantReviewDashboard from "@/pages/MerchantReviewDashboard";
import InfoRequestResponse from "@/pages/InfoRequestResponse";
import HostedPay from "@/pages/HostedPay";
import CockpitAgents from "@/pages/CockpitAgents";
import CockpitCommunications from "@/pages/CockpitCommunications";
import ClientCockpit from "@/pages/ClientCockpit";
import ToDo from "@/pages/ToDo";
import CockpitComingSoon from "@/components/CockpitComingSoon";
import ClientPortal from "@/pages/ClientPortal";
import MyBusinesses from "@/pages/MyBusinesses";
import Portfolio from "@/pages/Portfolio";
import Billing from "@/pages/Billing";
import Communications from "@/pages/Communications";
import AskClientAnswer from "@/pages/AskClientAnswer";
import ClientReviewPage from "@/pages/ClientReviewPage";
import EmbeddedCheckin from "@/pages/EmbeddedCheckin";
import CheckIn from "@/pages/CheckIn";
import CommunicationsPage from "@/pages/CommunicationsPage";
import CompliancePage from "@/pages/CompliancePage";
import CommunicationsDetailPage from "@/pages/CommunicationsDetailPage";
import SetPassword from "@/pages/SetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import ProTeam from "@/pages/ProTeam";
import CompanyTeam from "@/pages/CompanyTeam";
import GenericList from "@/pages/GenericList";
import FixedAssetsPage from "@/pages/FixedAssetsPage";
import GeneralLedger from "@/pages/GeneralLedger";
import Reconciliation from "@/pages/Reconciliation";
import ReconciliationDetail from "@/pages/ReconciliationDetail";
import BookReview from "@/pages/BookReview";
import ClosePeriods from "@/pages/ClosePeriods";
import PracticeHome from "@/pages/PracticeHome";
import PracticeSettings from "@/pages/PracticeSettings";
import PracticeContacts from "@/pages/PracticeContacts";
import PracticeNewContact from "@/pages/PracticeNewContact";
import PracticeContactDetail from "@/pages/PracticeContactDetail";
import PracticeMatters from "@/pages/PracticeMatters";
import PracticeNewMatter from "@/pages/PracticeNewMatter";
import PracticeMatterDetail from "@/pages/PracticeMatterDetail";
import PracticeCalendar from "@/pages/PracticeCalendar";
import PracticeActivities from "@/pages/PracticeActivities";
import PracticeWorkflows from "@/pages/PracticeWorkflows";
import PracticeWorkflowEdit from "@/pages/PracticeWorkflowEdit";
import PracticeInvoiceCreate from "@/pages/PracticeInvoiceCreate";
import PracticeTimeEntries from "@/pages/PracticeTimeEntries";
import PracticeUser from "@/pages/PracticeUser";
import PracticeNewInvoice from "@/pages/PracticeNewInvoice";
import PracticeInvoiceDetail from "@/pages/PracticeInvoiceDetail";
import PracticeInvoices from "@/pages/PracticeInvoices";
import PracticeExpenses from "@/pages/PracticeExpenses";
import PracticeMultipleExpenses from "@/pages/PracticeMultipleExpenses";
import { PracticePayments, PracticeBankAccounts, PracticeReconciliation, PracticePrintChecks } from "@/pages/PracticePaymentsPages";
import PracticeTimeEntryDetail from "@/pages/PracticeTimeEntryDetail";
import PracticeAddPayment from "@/pages/PracticeAddPayment";
import PracticeAddRetainer from "@/pages/PracticeAddRetainer";
import PracticeWriteCheck from "@/pages/PracticeWriteCheck";
import PracticeNewUser, { PracticeRoleForm } from "@/pages/PracticeUsersRoles";
import { PracticeAccess } from "@/components/practice/PracticeAccess";
import PracticePaymentDetail from "@/pages/PracticePaymentDetail";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Affiliate-only accounts see the Refer & earn page and nothing
  // else. Any deep-link into a client/pro surface bounces to /share.
  // The Layout component reads the same role and hides the sidebar.
  if (user.role === "affiliate" && pathname !== "/share") {
    return <Navigate to="/share" replace />;
  }
  return children;
}

// Root "/" landing: business owners (client role) default to the owner
// dashboard; everyone else to the accounting dashboard. Clients can still
// open /dashboard directly.
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === "client" ? "/owner" : user?.role === "pro" ? "/cockpit" : user?.role === "practice" ? "/practice" : "/dashboard"} replace />;
}

// Sits inside BrandingProvider so the hook can read the signed-in user's
// firm branding. Renders nothing — its only job is to keep document.title
// in sync with the current host + logged-in firm.
function HostTitle() { useHostTitle(); return null; }

function App() {
  useEffect(() => { initPwa(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
          <BrandingProvider>
          <EntitlementsProvider>
            <HostTitle />
            <InstallPromptToast />
            <UpgradeModal />
            <PlanPreviewPill />
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/book/:slug" element={<PublicBookingPage />} />
            <Route path="/demo/uk" element={<PublicDemoUK />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/affiliate" element={<Signup />} />
            <Route path="/signup/enterprise" element={<Signup />} />
            <Route path="/affiliates" element={<BecomeAffiliate />} />
            <Route path="/refer" element={<EnterReferral />} />
            <Route path="/refer/:slug" element={<EnterReferral />} />
            <Route path="/r/:slug" element={<ReferClickThru />} />
            <Route path="/set-password/:token" element={<SetPassword />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/q/:token" element={<AskClientAnswer />} />
            <Route path="/client-review/:token" element={<ClientReviewPage />} />
            <Route path="/portal/:token" element={<ClientPortal />} />
            <Route path="/pay/:token" element={<HostedPay />} />
            <Route path="/respond/:token" element={<InfoRequestResponse />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/billing/cancel" element={<BillingCancel />} />
            {/* Chrome-less onboarding pricing + payments pages — sit
                outside the Protected/Layout wrapper so no Sidebar /
                topbar / AI panel / floating chat renders on these
                routes. Auth is still required (Protected wraps just
                the element). Each page mounts its own local
                <Toaster/> so sonner toast calls still render. */}
            <Route path="/welcome/pricing" element={<Protected><PricingPlans /></Protected>} />
            <Route path="/welcome/payments" element={<Protected><PaymentsApplication /></Protected>} />
            <Route path="/welcome/complete" element={<Protected><WelcomeComplete /></Protected>} />
            <Route path="/practice" element={<Protected><PracticeAccess><PracticeHome /></PracticeAccess></Protected>} />
            <Route path="/practice/settings" element={<Protected><PracticeAccess><PracticeSettings /></PracticeAccess></Protected>} />
            <Route path="/practice/contacts" element={<Protected><PracticeAccess><PracticeContacts /></PracticeAccess></Protected>} />
            <Route path="/practice/contacts/new" element={<Protected><PracticeAccess><PracticeNewContact /></PracticeAccess></Protected>} />
            <Route path="/practice/contacts/:id" element={<Protected><PracticeAccess><PracticeContactDetail /></PracticeAccess></Protected>} />
            <Route path="/practice/matters" element={<Protected><PracticeAccess><PracticeMatters /></PracticeAccess></Protected>} />
            <Route path="/practice/matters/new" element={<Protected><PracticeAccess><PracticeNewMatter /></PracticeAccess></Protected>} />
            <Route path="/practice/matters/:id" element={<Protected><PracticeAccess><PracticeMatterDetail /></PracticeAccess></Protected>} />
            <Route path="/practice/matters/:id/edit" element={<Protected><PracticeAccess><PracticeNewMatter /></PracticeAccess></Protected>} />
            <Route path="/practice/calendar" element={<Protected><PracticeAccess><PracticeCalendar /></PracticeAccess></Protected>} />
            <Route path="/practice/activities" element={<Protected><PracticeAccess><PracticeActivities /></PracticeAccess></Protected>} />
            <Route path="/practice/workflows" element={<Protected><PracticeAccess><PracticeWorkflows /></PracticeAccess></Protected>} />
            <Route path="/practice/workflows/new" element={<Protected><PracticeAccess><PracticeWorkflowEdit /></PracticeAccess></Protected>} />
            <Route path="/practice/workflows/:id/edit" element={<Protected><PracticeAccess><PracticeWorkflowEdit /></PracticeAccess></Protected>} />
            <Route path="/practice/time-entries" element={<Protected><PracticeAccess><PracticeTimeEntries /></PracticeAccess></Protected>} />
            <Route path="/practice/time-entries/:id" element={<Protected><PracticeAccess><PracticeTimeEntryDetail /></PracticeAccess></Protected>} />
            <Route path="/practice/users/new" element={<Protected><PracticeAccess><PracticeNewUser /></PracticeAccess></Protected>} />
            <Route path="/practice/users/me" element={<Protected><PracticeAccess><PracticeUser /></PracticeAccess></Protected>} />
            <Route path="/practice/roles/new" element={<Protected><PracticeAccess><PracticeRoleForm /></PracticeAccess></Protected>} />
            <Route path="/practice/roles/:id" element={<Protected><PracticeAccess><PracticeRoleForm /></PracticeAccess></Protected>} />
            <Route path="/practice/invoices" element={<Protected><PracticeAccess><PracticeInvoices /></PracticeAccess></Protected>} />
            <Route path="/practice/invoices/create" element={<Protected><PracticeAccess><PracticeInvoiceCreate /></PracticeAccess></Protected>} />
            <Route path="/practice/expenses" element={<Protected><PracticeAccess><PracticeExpenses /></PracticeAccess></Protected>} />
            <Route path="/practice/expenses/multiple" element={<Protected><PracticeAccess><PracticeMultipleExpenses /></PracticeAccess></Protected>} />
            <Route path="/practice/payments" element={<Protected><PracticeAccess><PracticePayments /></PracticeAccess></Protected>} />
            <Route path="/practice/payments/accounts" element={<Protected><PracticeAccess><PracticeBankAccounts /></PracticeAccess></Protected>} />
            <Route path="/practice/payments/reconciliation" element={<Protected><PracticeAccess><PracticeReconciliation /></PracticeAccess></Protected>} />
            <Route path="/practice/payments/print-checks" element={<Protected><PracticeAccess><PracticePrintChecks /></PracticeAccess></Protected>} />
            <Route path="/practice/payments/new" element={<Protected><PracticeAccess><PracticeAddPayment /></PracticeAccess></Protected>} />
            <Route path="/practice/payments/retainer" element={<Protected><PracticeAccess><PracticeAddRetainer /></PracticeAccess></Protected>} />
            <Route path="/practice/payments/:src/:id" element={<Protected><PracticeAccess><PracticePaymentDetail /></PracticeAccess></Protected>} />
            <Route path="/practice/checks/new" element={<Protected><PracticeAccess><PracticeWriteCheck /></PracticeAccess></Protected>} />
            <Route path="/practice/invoices/:id/new" element={<Protected><PracticeAccess><PracticeNewInvoice /></PracticeAccess></Protected>} />
            <Route path="/practice/invoices/:id/edit" element={<Protected><PracticeAccess><PracticeNewInvoice /></PracticeAccess></Protected>} />
            <Route path="/practice/invoices/:id" element={<Protected><PracticeAccess><PracticeInvoiceDetail /></PracticeAccess></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/dashboard" element={<ProductGuard product="accounting"><Dashboard /></ProductGuard>} />
              <Route path="/owner" element={<OwnerDashboard />} />
              <Route path="/owner/:tab" element={<OwnerDashboard />} />
              <Route path="/accounting/todo" element={<ProductGuard product="accounting"><ToDo /></ProductGuard>} />
              <Route path="/accounting/projections" element={<ProductGuard product="accounting"><Gate feature="outlook" mode="replace"><Projections /></Gate></ProductGuard>} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/welcome/summary" element={<WelcomeSummary />} />
              <Route path="/pricing" element={<PricingPlans />} />
              <Route path="/admin" element={<SuperadminDash />} />
              <Route path="/admin/merchant-review" element={<MerchantReviewDashboard />} />
              <Route path="/admin/merchant-review/:bucket" element={<MerchantReviewList />} />
              <Route path="/admin/merchant-review/apps/:cid" element={<MerchantReviewDetail />} />
              <Route path="/admin/usage" element={<SuperadminUsage />} />
              <Route path="/admin/stripe-webhooks" element={<SuperadminStripeWebhooks />} />
              <Route path="/admin/qbo-gl-lab" element={<AdminQboGlLab />} />
              <Route path="/accounting/lab/review-v2" element={<ProductGuard product="accounting"><ReviewV2Lab /></ProductGuard>} />
              {/* Production alias — sidebar / cockpit / banners deep-link here.
                  Old /accounting/lab/review-v2 stays as an alias for existing
                  bookmarks and lab-mode CPAs. */}
              <Route path="/accounting/review" element={<ProductGuard product="accounting"><ReviewV2Lab /></ProductGuard>} />
              <Route path="/accounting/lab/transactions-compare" element={<ProductGuard product="accounting"><LabTransactionsCompare /></ProductGuard>} />
              <Route path="/partner" element={<PartnerDash />} />
              <Route path="/partner/financials" element={<PartnerFinancials />} />
              <Route path="/admin/partners/:pid" element={<AdminPartnerDetail />} />
              <Route path="/admin/enterprises/:eid" element={<AdminEnterpriseDetail />} />
              <Route path="/admin/client-payments" element={<AdminClientPayments />} />
              <Route path="/pro/client-payments" element={<AdminClientPayments />} />
              <Route path="/admin/feedback" element={<AdminFeedback />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/affiliates" element={<AdminAffiliates />} />
              <Route path="/admin/affiliate-toolkit" element={<AdminAffiliateToolkit />} />
              <Route path="/admin/cron-runs" element={<AdminCronRuns />} />
              <Route path="/admin/entitlements" element={<AdminEntitlements />} />
              <Route path="/feedback/mine" element={<MyFeedback />} />
              <Route path="/pro/clients" element={<ProClients />} />
              <Route path="/pro/onboarding" element={<ProOnboarding />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/estimates" element={<Estimates />} />
              <Route path="/estimates/new" element={<EstimateEditor />} />
              <Route path="/estimates/:id/edit" element={<EstimateEditor />} />
              <Route path="/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/purchase-orders/new" element={<PurchaseOrderEditor />} />
              <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderEditor />} />
              <Route path="/invoices/new" element={<InvoiceEditor />} />
              <Route path="/invoices/:id/edit" element={<InvoiceEditor />} />
              <Route path="/bills" element={<Bills />} />
              <Route path="/bills/new" element={<BillEditor />} />
              <Route path="/bills/:id/edit" element={<BillEditor />} />
              <Route path="/purchases/new" element={<AdvancedModeRoute><PurchaseEditor /></AdvancedModeRoute>} />
              <Route path="/purchases/:id/edit" element={<AdvancedModeRoute><PurchaseEditor /></AdvancedModeRoute>} />
              <Route path="/sales-receipts/new" element={<AdvancedModeRoute><SalesReceiptEditor /></AdvancedModeRoute>} />
              <Route path="/sales-receipts/:id/edit" element={<AdvancedModeRoute><SalesReceiptEditor /></AdvancedModeRoute>} />
              <Route path="/sales-receipts" element={<AdvancedModeRoute><SalesReceipts /></AdvancedModeRoute>} />
              <Route path="/deposits/new" element={<AdvancedModeRoute><DepositEditor /></AdvancedModeRoute>} />
              <Route path="/deposits/:id/edit" element={<AdvancedModeRoute><DepositEditor /></AdvancedModeRoute>} />
              <Route path="/credit-memos/new" element={<AdvancedModeRoute><CreditMemoEditor /></AdvancedModeRoute>} />
              <Route path="/credit-memos/:id/edit" element={<AdvancedModeRoute><CreditMemoEditor /></AdvancedModeRoute>} />
              <Route path="/credit-memos" element={<AdvancedModeRoute><CreditMemos /></AdvancedModeRoute>} />
              <Route path="/refund-receipts/new" element={<AdvancedModeRoute><RefundReceiptEditor /></AdvancedModeRoute>} />
              <Route path="/refund-receipts/:id/edit" element={<AdvancedModeRoute><RefundReceiptEditor /></AdvancedModeRoute>} />
              <Route path="/accounting/bank-matches" element={<AdvancedModeRoute><BankMatchReview /></AdvancedModeRoute>} />
              <Route path="/recurring" element={<Recurring />} />
              <Route path="/items" element={<Items />} />
              <Route path="/inventory-management" element={<Gate feature="inventory" mode="replace"><InventoryPage /></Gate>} />
              <Route path="/sales-reports" element={<SalesReports />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/receipts" element={<Receipts />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/contacts/:contactId" element={<Contacts />} />
              <Route path="/customer-statements" element={<CustomerStatements />} />
              <Route path="/connections" element={<Connections />} />
              <Route path="/connections/qbo" element={<QboConnect />} />
              <Route path="/test-qbo" element={<TestQbo />} />
              <Route path="/vendor-credits" element={<VendorCredits />} />
              <Route path="/vendor-credits/new" element={<AdvancedModeRoute><VendorCreditEditor /></AdvancedModeRoute>} />
              <Route path="/vendor-credits/:id/edit" element={<AdvancedModeRoute><VendorCreditEditor /></AdvancedModeRoute>} />
              <Route path="/refund-receipts" element={<RefundReceipts />} />
              <Route path="/accounting/sales-tax" element={<Gate feature="sales_tax" mode="replace"><SalesTax /></Gate>} />
              <Route path="/settings/qbo-mirror" element={<QboMirror />} />
              <Route path="/settings/pfc-map" element={<PfcCategoryMap />} />
              <Route path="/settings/notifications" element={<NotificationSettings />} />
              <Route path="/connections/imports/:importId" element={<StatementImportDetail />} />
              <Route path="/settings" element={<Navigate to="/accounting/settings" replace />} />
              <Route path="/completed-actions" element={<CompletedActions />} />
              <Route path="/audit-log" element={<AuditLog />} />
              <Route path="/pro/settings" element={<ProSettings />} />
              <Route path="/pro/team" element={<ProTeam />} />
              <Route path="/pro/affiliates" element={<ProAffiliates />} />
              <Route path="/company-team" element={<CompanyTeam />} />
              <Route path="/communications-audit" element={<Communications />} />
              {/* Back-compat: old bookmark for the outbound-email
                  audit log (renamed Sep 2026 so the top-level
                  /communications URL can host the client-review
                  transcript archive). */}
              <Route path="/communications/audit" element={<Communications />} />
              <Route path="/reports" element={<TrialReportsGate><Reports /></TrialReportsGate>} />
              <Route path="/reports/ar-aging" element={<ArAgingReport />} />
              <Route path="/reports/ap-aging" element={<ApAgingReport />} />
              <Route path="/reports/sales-tax-report" element={<SalesTaxReport />} />
              <Route path="/reports/book-to-tax" element={<BookToTaxReport />} />
              <Route path="/reports/tax-lines" element={<TaxLineReport />} />
              <Route path="/reports/:kind" element={<ReportView />} />
              <Route path="/accounting/transactions" element={<Transactions />} />
              <Route path="/accounting/payroll" element={<ProductGuard product="accounting"><PayrollDashboard /></ProductGuard>} />
              <Route path="/accounting/payroll/runs" element={<ProductGuard product="accounting"><PayrollRuns /></ProductGuard>} />
              <Route path="/accounting/payroll/runs/:id" element={<ProductGuard product="accounting"><PayrollRun /></ProductGuard>} />
              <Route path="/accounting/payroll/employees/:eid" element={<ProductGuard product="accounting"><PayrollEmployeeHistory /></ProductGuard>} />
              <Route path="/accounting/ai-cleanup-review" element={<AICleanupReview />} />
              <Route path="/accounting/check-register-review" element={<CheckRegisterReview />} />
              <Route path="/accounting/review-chat" element={<ChatReview />} />
              <Route path="/checkin" element={<ProductGuard product="accounting"><Gate feature="checkins" mode="replace"><CheckIn /></Gate></ProductGuard>} />
              <Route path="/accounting/liability-payments" element={<Gate feature="liability_ai" mode="replace"><EmbeddedCheckin scope="liability_payments" /></Gate>} />
              <Route path="/accounting/receipt-followup" element={<EmbeddedCheckin scope="receipt_followup" />} />
              <Route path="/accounting/checks-review" element={<EmbeddedCheckin scope="checks" />} />
              <Route path="/accounting/lets-review" element={<LetsReview />} />
              <Route path="/accounting/no-contact-review" element={<NoContactReview />} />
              <Route path="/accounting/transfer-review" element={<TransferReview />} />
              <Route path="/accounting/inventory" element={<GenericList
                path="inventory" title="Inventory"
                fields={[{k:"name",l:"Item Name"},{k:"sku",l:"SKU"},{k:"quantity",l:"Qty",t:"number"},{k:"unit_cost",l:"Unit Cost",t:"number"}]}
              />} />
              <Route path="/accounting/assets" element={<FixedAssetsPage />} />
              <Route path="/accounting/loans" element={<LoansPage />} />
              <Route path="/accounting/tags" element={<GenericList
                path="tags" title="Tags"
                fields={[{k:"name",l:"Tag"},{k:"description",l:"Description"}]}
              />} />
              <Route path="/accounting/reconciliation" element={<Reconciliation />} />
              <Route path="/accounting/reconciliation/:rid" element={<ReconciliationDetail />} />
              <Route path="/accounting/book-review" element={<BookReview />} />
              <Route path="/accounting/close-books" element={<ClosePeriods kind="month" />} />
              <Route path="/accounting/month-close" element={<MonthClose />} />
              {/* Cockpit — cross-client command surface (Feb 2026).
                  Hidden in the sidebar for single-book client-owners;
                  backend rejects them with 403 anyway. */}
              <Route path="/cockpit" element={<CockpitLayout />}>
                <Route index element={<CockpitTodayV7 />} />
                <Route path="today" element={<CockpitTodayV7 />} />
                <Route path="today-v7" element={<CockpitTodayV7 />} />
                <Route path="assistant" element={<CockpitAssistantAll />} />
                <Route path="close" element={<CockpitCloseBoard />} />
                <Route path="close/:companyId" element={<CockpitCloseBoard />} />
                <Route path="requests" element={<CockpitRequests />} />
                <Route path="1099" element={<Cockpit1099 />} />
                <Route path="reports" element={<CockpitReports />} />
                <Route path="agents" element={<CockpitAgents />} />
                <Route path="communications" element={<CockpitCommunications />} />
                <Route path="payments-apps" element={<CockpitPaymentsApps />} />
                <Route path="client" element={<ClientCockpit />} />
                <Route path="practice-health" element={<CockpitComingSoon
                  testid="cockpit-practice-health-page"
                  phase="Phase 2+"
                  title="Practice Health"
                  tagline="Firm-level KPIs: books-on-time rate, average close cycle, client health scores, realization by client, team utilization. Turns your firm from an ops black-box into a managed operation."
                  features={[
                    "Books-on-time rate (rolling 6 months)",
                    "Average close cycle time",
                    "Client health score distribution",
                    "Realization by client (revenue vs. hours × rate)",
                    "Team utilization dashboard",
                  ]}
                />} />
              </Route>
              <Route path="/my-businesses" element={<MyBusinesses />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/share" element={<Share />} />
              <Route path="/accounting/year-end" element={<ClosePeriods kind="year" />} />
              <Route path="/accounting/chart-of-accounts" element={<ChartOfAccounts />} />
              <Route path="/accounting/classes" element={<Gate feature="classes" mode="replace"><Classes /></Gate>} />
              <Route path="/accounting/projects" element={<ProductGuard product="projects"><ProjectsDashboard /></ProductGuard>} />
              <Route path="/accounting/projects/list" element={<Projects />} />
              <Route path="/accounting/projects/:projectId" element={<ProjectDetail />} />
              <Route path="/accounting/budgets" element={<Gate feature="budgets" mode="replace"><Budgets /></Gate>} />
              <Route path="/accounting/budgets/:budgetId" element={<Gate feature="budgets" mode="replace"><BudgetEditor /></Gate>} />
              <Route path="/crm" element={<ProductGuard product="crm"><CrmOverview /></ProductGuard>} />
              <Route path="/home" element={<ProductGuard product="home"><HomeDashboard /></ProductGuard>} />
              <Route path="/crm/deals" element={<DealsBoard />} />
              <Route path="/crm/email" element={<CrmEmail />} />
              <Route path="/crm/calendar" element={<CrmCalendar />} />
              <Route path="/crm/settings" element={<CrmSettings />} />
              <Route path="/accounting/settings" element={<AccountingSettings />} />
              <Route path="/admin/product-launches" element={<AdminProductLaunches />} />
              <Route path="/team" element={<ProductGuard product="team"><Team /></ProductGuard>} />
              <Route path="/team/time" element={<TimeLog />} />
              <Route path="/team/calendar" element={<TeamCalendar />} />
              <Route path="/team/approvals" element={<TimesheetApprovals />} />
              <Route path="/reports/budget-vs-actuals" element={<BudgetVsActuals />} />
              <Route path="/reports/estimates-vs-actuals" element={<EstimatesVsActuals />} />
              <Route path="/accounting/journal-entries" element={<JournalEntries />} />
              <Route path="/accounting/checks" element={<PrintChecks />} />
              <Route path="/accounting/general-ledger" element={<GeneralLedger />} />
              <Route path="/accounting/rules" element={<Rules />} />
              {/* Communications — cross-client transcript archive of
                  every batch review the platform has produced. */}
              <Route path="/communications" element={<CommunicationsPage />} />
              <Route path="/communications/:threadId" element={<CommunicationsDetailPage />} />
              {/* Compliance — IRS documentation library (§274 meals /
                  travel / vehicle / gifts, §170 charitable). Ships
                  with Meals live + placeholders for the other categories. */}
              <Route path="/compliance" element={<CompliancePage />} />
              {/* Feb 2026: Tax Library merged into Sales Tax Center's
                  Rates tab. Preserve the old bookmark by redirecting. */}
              <Route path="/accounting/taxes" element={<Navigate to="/accounting/sales-tax?tab=rates" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <VoiceActionReview />
          <VoiceRecapReview />
          </EntitlementsProvider>
          </BrandingProvider>
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
